<template>
	<div>
		<div class="mb-4">
			<h6 class="text-lg uppercase tracking-wider text-gray-500 mb-1">Accesorii garduri</h6>
			<v-label class="text-base">
				Automatizari, cutii postale, module wifi, telecomenzi si orice alt accesoriu care poate fi
				adaugat pe o oferta. <br>Fiecare accesoriu are un rand pe tara, cu denumirea si pretul din tara
				respectiva &mdash; oferta preia direct randul tarii ei.
				<span class="block mt-2">Preturile sunt <span class="font-bold">fara TVA</span>, in EUR.</span>
			</v-label>
		</div>

		<div v-if="loading" class="progress">
			<div class="indeterminate"></div>
		</div>

		<div v-else-if="error">
			<p class="text-red-500 mb-4">{{ error }}</p>
			<v-btn color="teal" class="uppercase white--text" @click="load">Reincarca</v-btn>
		</div>

		<div v-else>
			<div class="flex justify-between items-center mb-4">
				<v-btn color="teal" class="uppercase white--text" @click="openNewProduct">
					<v-icon left small>mdi-plus</v-icon>Adauga accesoriu
				</v-btn>
				<v-btn type="button" color="teal" class="uppercase white--text" :loading="saving"
					:disabled="saving || !hasChanges" @click="save">Salveaza</v-btn>
			</div>

			<v-form ref="formAccessories" v-model="validForm" @submit.prevent="save">
				<div v-for="product in products" :key="product.code" class="bg-white border rounded-lg p-4 mb-4">
					<div class="flex justify-between items-center mb-3">
						<div>
							<span class="font-bold">{{ product.code }}</span>
							<span class="text-sm text-gray-500 ml-2">{{ product.countries.length }} tari</span>
						</div>
						<v-btn icon small title="Sterge accesoriul din toate tarile"
							@click="confirmDelete(product.code)">
							<v-icon small>mdi-delete</v-icon>
						</v-btn>
					</div>

					<table class="w-full">
						<thead>
							<tr class="text-left text-gray-500 text-sm">
								<th class="pb-2 w-16">Tara</th>
								<th class="pb-2">Denumire</th>
								<th class="pb-2 w-44">Pret fără TVA</th>
								<th class="pb-2 w-28">UM</th>
								<th class="pb-2 w-24">Activ</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in product.countries" :key="row.key">
								<td class="py-1 pr-2 align-middle font-bold">{{ row.country }}</td>
								<td class="py-1 pr-2 align-middle">
									<v-text-field v-model="edited[row.key].name" dense outlined hide-details="auto"
										:rules="[rules.required]"></v-text-field>
								</td>
								<td class="py-1 pr-2 align-middle">
									<v-text-field v-model="edited[row.key].price" dense outlined type="number"
										step="0.01" hide-spin-buttons suffix="EUR" hide-details="auto"
										:rules="[rules.required, rules.price]"></v-text-field>
								</td>
								<td class="py-1 pr-2 align-middle">
									<v-select v-model="edited[row.key].unit" :items="units" dense outlined
										hide-details="auto"></v-select>
								</td>
								<td class="py-1 pr-2 align-middle">
									<v-switch v-model="edited[row.key].active" :true-value="1" :false-value="0"
										color="teal" class="mt-0" hide-details></v-switch>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</v-form>

			<p v-if="products.length < 1" class="text-gray-500">Nu exista accesorii definite.</p>
		</div>

		<!-- a new accessory needs a name in every country, since the column is required -->
		<v-dialog v-model="showNewProduct" max-width="640px">
			<v-card class="p-6">
				<h6 class="text-xl font-bold mb-4">Accesoriu nou</h6>
				<v-form ref="formNewProduct" v-model="validNewProduct" @submit.prevent="createProduct">
					<div class="flex flex-wrap gap-2 mb-4">
						<!-- uppercased as it is typed, so what is saved is what the field shows -->
						<v-text-field :value="newProduct.code" @input="setCode" class="w-56 flex-grow-0" label="Cod"
							dense outlined placeholder="BFT_VIRGO" hide-details="auto"
							:rules="[rules.required, rules.code]"></v-text-field>
						<v-text-field v-model="newProduct.price" class="w-44 flex-grow-0" label="Pret fără TVA"
							type="number" step="0.01" suffix="EUR" dense outlined hide-spin-buttons hide-details="auto"
							:rules="[rules.required, rules.price]"></v-text-field>
						<v-select v-model="newProduct.unit" :items="units" class="w-28 flex-grow-0" label="UM" dense
							outlined hide-details="auto"></v-select>
					</div>

					<div class="flex flex-col gap-2 mb-4">
						<v-text-field v-for="country in countries" :key="country" v-model="newProduct.names[country]"
							:label="'Denumire ' + country" dense outlined hide-details="auto"
							:rules="[rules.required]"></v-text-field>
					</div>

					<div class="flex justify-end gap-2">
						<v-btn class="uppercase" @click="showNewProduct = false">Anuleaza</v-btn>
						<v-btn type="submit" color="teal" class="uppercase white--text" :loading="saving">Adauga</v-btn>
					</div>
				</v-form>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
/**
 * The fence accessories catalogue.
 *
 * The table holds one row per country per product, so this screen groups by code and edits the
 * country rows together - that is where an accessory's name and price for a given country live,
 * and what an offer picks up directly.
 */
const COUNTRIES = ["RO", "AT", "DE", "HU", "FR"];

export default {
	name: "FenceAccessoriesSettings",
	data() {
		return {
			validForm: false,
			validNewProduct: false,
			showNewProduct: false,
			// keyed "CODE|COUNTRY", the editable copy of what the store holds
			edited: {},
			// image is deliberately absent: the filename is set in the database, never from here,
			// so an accidental edit cannot break an accessory's picture on a printed offer
			newProduct: { code: "", price: "", unit: "buc", names: {} },
			rules: {
				required: value => (value !== "" && value !== null && value !== undefined)
					|| "Câmp obligatoriu",
				price: value => (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)
					|| "Valoare invalida",
				code: value => /^[A-Za-z0-9_-]{1,64}$/.test(String(value || ""))
					|| "Litere, cifre, _ si - ",
			},
		};
	},
	computed: {
		section() {
			return this.$store.state.fenceAccessories;
		},
		loading() {
			return this.section.loadingAccessories;
		},
		saving() {
			return this.section.savingAccessories;
		},
		error() {
			return this.section.accessoriesError;
		},
		accessories() {
			return this.section.accessories;
		},
		units() {
			return this.$store.getters.ums.map(um => um.value);
		},
		/** The countries the catalogue is kept in, taken from the data so it follows it. */
		countries() {
			const found = this.accessories
				.map(row => row.country)
				.filter((country, index, all) => all.indexOf(country) === index);

			return found.length > 0 ? found.sort() : COUNTRIES;
		},
		/** One entry per product, carrying its country rows. */
		products() {
			const byCode = {};

			this.accessories.forEach(row => {
				if (!byCode[row.code]) {
					byCode[row.code] = { code: row.code, sort_order: row.sort_order, countries: [] };
				}
				byCode[row.code].countries.push({
					key: this.keyFor(row.code, row.country),
					country: row.country
				});
			});

			return Object.keys(byCode)
				.map(code => byCode[code])
				.sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code));
		},
		hasChanges() {
			return this.buildChanges().length > 0;
		},
	},
	methods: {
		keyFor(code, country) {
			return code + "|" + country;
		},
		/** Codes are uppercase everywhere in the catalogue, so a new one is uppercased on the way in. */
		setCode(value) {
			this.newProduct.code = String(value || "").toUpperCase();
		},
		load() {
			this.$store.dispatch("fenceAccessories/fetchAccessories");
		},
		syncFromStore() {
			const edited = {};

			this.accessories.forEach(row => {
				edited[this.keyFor(row.code, row.country)] = {
					name: row.name,
					price: row.price,
					unit: row.unit,
					active: row.active
				};
			});

			this.edited = edited;
		},
		/** Only the rows whose fields actually differ, and within a row only the changed keys. */
		buildChanges() {
			const changes = [];

			this.accessories.forEach(row => {
				const current = this.edited[this.keyFor(row.code, row.country)];

				if (!current) return;

				const stored = {
					name: row.name,
					price: row.price,
					unit: row.unit,
					active: row.active
				};
				const changed = {};

				Object.keys(stored).forEach(field => {
					const value = field === "price" || field === "active"
						? Number(current[field])
						: current[field];

					if (value !== stored[field]) changed[field] = value;
				});

				if (Object.keys(changed).length > 0) {
					changes.push(Object.assign({ code: row.code, country: row.country }, changed));
				}
			});

			return changes;
		},
		async save() {
			const valid = await this.$refs.formAccessories.validate();

			if (valid) {
				await this.$store.dispatch("fenceAccessories/saveAccessories", this.buildChanges());
			}
		},
		openNewProduct() {
			const names = {};
			this.countries.forEach(country => { names[country] = "" });

			this.newProduct = { code: "", price: "", unit: "buc", names: names };
			this.showNewProduct = true;
		},
		async createProduct() {
			const valid = await this.$refs.formNewProduct.validate();

			if (!valid) return;

			// one row per country, so the product exists everywhere from the start
			const rows = this.countries.map(country => ({
				code: this.newProduct.code.trim().toUpperCase(),
				country: country,
				name: this.newProduct.names[country],
				price: Number(this.newProduct.price),
				unit: this.newProduct.unit
			}));

			const saved = await this.$store.dispatch("fenceAccessories/saveAccessories", rows);

			if (saved) this.showNewProduct = false;
		},
		confirmDelete(code) {
			if (window.confirm(`Stergi accesoriul ${code} din toate tarile?`)) {
				this.$store.dispatch("fenceAccessories/deleteAccessory", code);
			}
		},
	},
	watch: {
		accessories: {
			handler: "syncFromStore",
			deep: true
		},
	},
	created() {
		this.load();
	},
};
</script>

<style scoped></style>
